// Helper-API для Web Push в браузере (ANX-016).
//
// Жизненный цикл подписки:
//   1. isPushSupported(): фича-детект (Notification + ServiceWorker + PushManager).
//   2. registerServiceWorker(): зарегистрировать /sw.js (один раз).
//   3. ensurePushSubscription(): запросить permission → subscribe →
//      отправить endpoint+keys на backend (upsert).
//   4. disablePush(): отменить браузерную подписку → удалить из backend'а.
//
// Все шаги независимы от UI; SettingsPage оборачивает их с состояниями.

import { getApiClient } from './api/factory';
import {
  createPushSubscription,
  deletePushSubscription,
  getVAPIDPublicKey,
  listPushSubscriptions,
  type StoredPushSubscription,
} from './api/push';

export type PushStatus =
  | 'unsupported'        // браузер не умеет push
  | 'server_disabled'    // backend без VAPID
  | 'permission_denied'  // пользователь явно отказал
  | 'subscribed'         // активная подписка зарегистрирована в backend'е
  | 'idle';              // поддерживается, но пока не подписан

export interface PushState {
  status: PushStatus;
  /** id подписки в backend'е, если subscribed. */
  subscriptionId?: string;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  // vite-plugin-pwa с injectRegister:false генерирует sw.js в корне dist.
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

/**
 * Считывает текущее состояние:
 *  - server has VAPID?
 *  - permission?
 *  - subscription in browser?
 *  - registered in backend (matching endpoint)?
 *
 * Не мутирует никакое состояние браузера/сервера.
 */
export async function getPushState(): Promise<PushState> {
  if (!isPushSupported()) return { status: 'unsupported' };

  const client = getApiClient();
  const vapid = await getVAPIDPublicKey(client);
  if (!vapid) return { status: 'server_disabled' };

  if (Notification.permission === 'denied') {
    return { status: 'permission_denied' };
  }

  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return { status: 'idle' };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { status: 'idle' };

  // Проверим, что endpoint известен backend'у (мог быть удалён вручную
  // из других сессий — тогда в UI покажем idle с возможностью пересоздать).
  try {
    const stored = await listPushSubscriptions(client);
    const match = stored.find((s) => s.endpoint === sub.endpoint);
    if (match) return { status: 'subscribed', subscriptionId: match.id };
  } catch {
    // backend недоступен — UI обработает на другом уровне.
  }
  return { status: 'idle' };
}

/**
 * Включает push-уведомления end-to-end.
 *
 * Идемпотентна: повторный вызов с уже активной браузерной подпиской
 * просто перезаписывает регистрацию в backend (upsert по endpoint).
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return { status: 'unsupported' };
  const client = getApiClient();

  const vapid = await getVAPIDPublicKey(client);
  if (!vapid) return { status: 'server_disabled' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { status: permission === 'denied' ? 'permission_denied' : 'idle' };
  }

  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) {
    reg = (await registerServiceWorker()) ?? undefined;
  }
  if (!reg) return { status: 'unsupported' };
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  const stored = await createPushSubscription(client, {
    endpoint: sub.endpoint,
    keys: extractKeys(sub),
    user_agent: navigator.userAgent,
  });
  return { status: 'subscribed', subscriptionId: stored.id };
}

/**
 * Отключает push: убирает браузерную подписку и удаляет запись из
 * backend'а (если есть). Идемпотентна.
 */
export async function disablePush(): Promise<PushState> {
  if (!isPushSupported()) return { status: 'unsupported' };
  const client = getApiClient();

  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = reg ? await reg.pushManager.getSubscription() : null;

  if (sub) {
    let stored: StoredPushSubscription | undefined;
    try {
      const all = await listPushSubscriptions(client);
      stored = all.find((s) => s.endpoint === sub.endpoint);
    } catch {
      // продолжаем — браузерная отписка важнее
    }
    try {
      await sub.unsubscribe();
    } catch {
      // даже если браузер вернул false — мы зачистим backend
    }
    if (stored) {
      try {
        await deletePushSubscription(client, stored.id);
      } catch {
        // backend мог уже удалить (race с worker'ом на 410)
      }
    }
  }

  if (Notification.permission === 'denied') {
    return { status: 'permission_denied' };
  }
  return { status: 'idle' };
}

/**
 * Преобразует base64url-string (VAPID public key) в Uint8Array,
 * ожидаемый pushManager.subscribe().
 *
 * Web Crypto не имеет встроенного base64url-decode, потому делаем
 * руками: padding до длины кратной 4 + замена '-' '_' на '+' '/'.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  // Явно создаём ArrayBuffer-backed Uint8Array — Push API в DOM-типах
  // не принимает SharedArrayBuffer.
  const buf = new ArrayBuffer(rawData.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < rawData.length; i++) bytes[i] = rawData.charCodeAt(i);
  return bytes;
}

function extractKeys(sub: PushSubscription): { p256dh: string; auth: string } {
  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!p256dh || !auth) {
    throw new Error('push subscription missing keys');
  }
  return {
    p256dh: arrayBufferToBase64Url(p256dh),
    auth: arrayBufferToBase64Url(auth),
  };
}

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

// Экспортированные хелперы только для unit-тестов.
export const _internals = { urlBase64ToUint8Array, arrayBufferToBase64Url };
